import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  CircularProgress,
  CircularProgressLabel,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  List,
  ListItem,
  Progress,
  Skeleton,
  SkeletonText,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useCallback, useRef, useState } from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Icon from 'components/Icon';
import { ProtectedElement } from 'components/ProtectedElement';
import Text from 'components/Text';
import { TanstackQueryName } from 'types/frontend';

import { useCVStream } from '../hooks/useCVStream';
import { CVAIReport, CVAIReportSection } from '../types';

interface AIReportPanelProps {
  isOpen: boolean;
  onClose: () => void;
  cvId: string;
  report: CVAIReport | null;
}

export const getScoreColor = (score: number): string => {
  if (score >= 80) return 'green.400';
  if (score >= 60) return 'yellow.400';

  return 'red.400';
};

export const getScoreColorScheme = (score: number): string => {
  if (score >= 80) return 'green';
  if (score >= 60) return 'yellow';

  return 'red';
};

export const getRatingColor = (rating: string): string => {
  if (rating === 'strong') return 'green';
  if (rating === 'moderate') return 'yellow';

  return 'red';
};

export const getScoreLabel = (score: number): string => {
  if (score >= 80) return 'Well Optimized';
  if (score >= 60) return 'Needs Improvement';

  return 'Significant Changes Needed';
};

export const getATSTips = (score: number): string[] => {
  const tips: string[] = [];

  if (score < 80) {
    tips.push('Use standard section headings like "Work Experience", "Education", "Skills" — ATS parsers rely on common names.');
  }
  if (score < 70) {
    tips.push('Avoid using tables, columns, or text boxes — most ATS cannot parse multi-column layouts correctly.');
  }
  if (score < 60) {
    tips.push('Include a professional summary with 2-3 sentences that include your target role title and key skills.');
  }

  tips.push('Start each bullet point with a strong action verb (Led, Built, Designed, Optimized, Delivered).');
  tips.push('Add quantifiable metrics: percentages, dollar amounts, team sizes, or time savings to demonstrate impact.');
  tips.push('Mirror keywords from the job description in your experience and skills sections.');

  if (score < 50) {
    tips.push('Ensure your contact information is complete — full name, email, phone, and location are expected by ATS.');
    tips.push('Keep your CV to 1-2 pages. Long CVs are often truncated by ATS before reaching a recruiter.');
  }

  return tips;
};

/** Maps SSE progress stages to a 0–100 progress bar value. */
export const STAGE_PROGRESS: Record<string, number> = {
  analyzing: 10,
  scoring: 25,
  summarizing: 40,
  reviewing_sections: 55,
  finding_keywords: 70,
  generating_suggestions: 85,
  saving: 95,
};

/** Maps SSE progress stages to user-friendly labels. */
const STAGE_LABELS: Record<string, string> = {
  analyzing: 'Analyzing your CV...',
  scoring: 'Calculating ATS score...',
  summarizing: 'Writing summary...',
  reviewing_sections: 'Reviewing sections...',
  finding_keywords: 'Identifying keywords...',
  generating_suggestions: 'Generating suggestions...',
  saving: 'Saving review...',
};

const SectionHeader = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
  <HStack spacing={2} mb={3}>
    <Box color="purple.400" display="flex" alignItems="center" flexShrink={0}>
      {icon}
    </Box>
    <Text fontSize="medium" fontWeight="semibold">
      {title}
    </Text>
  </HStack>
);

const SectionSkeleton = () => (
  <Box p={4} borderRadius="md" backgroundColor="whiteAlpha.50" border="1px solid" borderColor="whiteAlpha.100">
    <SkeletonText noOfLines={3} spacing="3" startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
  </Box>
);

const AIReportPanel = ({ isOpen, onClose, cvId, report }: AIReportPanelProps) => {
  const queryClient = useQueryClient();
  const { toastError } = useToastWrapper();
  const { id: currentUserId } = useCurrentUser();

  // Streaming state
  const [streamingReport, setStreamingReport] = useState<Partial<CVAIReport>>({});
  const [reviewStage, setReviewStage] = useState<string | null>(null);
  const [progressPercent, setProgressPercent] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const hasInvalidatedTokenRef = useRef(false);

  const handleSection = useCallback((sectionType: string, data: unknown) => {
    setStreamingReport((prev) => {
      const next = { ...prev };

      switch (sectionType) {
        case 'atsScore':
          next.atsScore = data as number;
          break;
        case 'overallSummary':
          next.overallSummary = data as string;
          break;
        case 'sectionBreakdown':
          next.sectionBreakdown = data as CVAIReportSection[];
          break;
        case 'missingKeywords':
          next.missingKeywords = data as string[];
          break;
        case 'suggestions':
          next.suggestions = data as string[];
          break;
      }

      return next;
    });
  }, []);

  const handleProgress = useCallback(
    (stage: string) => {
      // Token is consumed server-side before first SSE event — refresh topbar balance once
      if (!hasInvalidatedTokenRef.current) {
        hasInvalidatedTokenRef.current = true;
        queryClient.invalidateQueries({
          queryKey: [TanstackQueryName.GetCurrentUser, currentUserId],
        });
      }

      setReviewStage(stage);
      const pct = STAGE_PROGRESS[stage];

      if (pct !== undefined) {
        setProgressPercent(pct);
      }
    },
    [queryClient, currentUserId]
  );

  const handleDone = useCallback(() => {
    setProgressPercent(100);
    setReviewStage(null);
    setIsDone(true);

    queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetCV, cvId] });
  }, [queryClient, cvId]);

  const handleError = useCallback(
    (message: string) => {
      setReviewStage(null);
      toastError(message || 'Could not generate review. Please try again.');
    },
    [toastError]
  );

  const { sendRequest, isStreaming } = useCVStream({
    onSection: handleSection,
    onProgress: handleProgress,
    onDone: handleDone,
    onError: handleError,
  });

  const handleGenerateReview = useCallback(() => {
    setStreamingReport({});
    setReviewStage(null);
    setProgressPercent(0);
    setIsDone(false);
    hasInvalidatedTokenRef.current = false;

    sendRequest('review-cv', { cvId });
  }, [sendRequest, cvId]);

  // Don't cancel stream when drawer closes — token is already consumed,
  // let the server finish and save the report to DB

  // The report to display: use streaming data while streaming, otherwise the DB report
  const displayReport: CVAIReport | null =
    isStreaming || (reviewStage === null && streamingReport.atsScore !== undefined)
      ? {
          atsScore: streamingReport.atsScore ?? 0,
          overallSummary: streamingReport.overallSummary ?? '',
          sectionBreakdown: streamingReport.sectionBreakdown ?? [],
          missingKeywords: streamingReport.missingKeywords ?? [],
          suggestions: streamingReport.suggestions ?? [],
          generatedAt: new Date().toISOString(),
        }
      : report;

  const hasReport = displayReport !== null;
  const hasStreamedSomething = streamingReport.atsScore !== undefined;

  // During streaming, determine which sections have arrived
  const isStreamingMode = isStreaming || (hasStreamedSomething && reviewStage === null);
  const hasAtsScore = isStreamingMode ? streamingReport.atsScore !== undefined : hasReport;
  const hasSummary = isStreamingMode ? streamingReport.overallSummary !== undefined : hasReport;
  const hasBreakdown = isStreamingMode
    ? streamingReport.sectionBreakdown !== undefined
    : hasReport && (displayReport?.sectionBreakdown?.length ?? 0) > 0;
  const hasKeywords = isStreamingMode
    ? streamingReport.missingKeywords !== undefined
    : hasReport && (displayReport?.missingKeywords?.length ?? 0) > 0;
  const hasSuggestions = isStreamingMode
    ? streamingReport.suggestions !== undefined
    : hasReport && (displayReport?.suggestions?.length ?? 0) > 0;

  const renderEmptyState = () => (
    <VStack spacing={6} py={10} textAlign="center">
      <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
        <circle cx="40" cy="40" r="35" stroke="#6B46C1" strokeWidth="2" strokeDasharray="4 4" />
        <path d="M30 40h20M40 30v20" stroke="#6B46C1" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <VStack spacing={2}>
        <Text fontSize="large" fontWeight="semibold">
          No AI Review Yet
        </Text>
        <Text fontSize="small" opacity="secondary">
          Get an AI-powered analysis of your CV including ATS compatibility score, keyword coverage,
          and actionable improvement suggestions.
        </Text>
      </VStack>
      <ProtectedElement
        scheme="gradient"
        onClick={handleGenerateReview}
        isLoading={isStreaming}
        requiredTokens={1}
      >
        Generate AI Review
      </ProtectedElement>
    </VStack>
  );

  const renderATSScore = () => {
    if (!hasReport && !isStreaming) return null;

    if (isStreaming && !hasAtsScore) {
      return (
        <VStack spacing={3}>
          <Skeleton
            width="120px"
            height="120px"
            borderRadius="full"
            startColor="whiteAlpha.100"
            endColor="whiteAlpha.200"
          />
          <Skeleton
            width="140px"
            height="24px"
            borderRadius="full"
            startColor="whiteAlpha.100"
            endColor="whiteAlpha.200"
          />
        </VStack>
      );
    }

    if (!displayReport || displayReport.atsScore === 0) return null;

    return (
      <VStack spacing={3}>
        <CircularProgress
          value={displayReport.atsScore}
          size="120px"
          thickness="8px"
          color={getScoreColor(displayReport.atsScore)}
          trackColor="whiteAlpha.200"
        >
          <CircularProgressLabel>
            <VStack spacing={0}>
              <Text fontSize="xxLarge" fontWeight="bold">
                {displayReport.atsScore}
              </Text>
              <Text fontSize="xMicro" opacity="tertiary">
                ATS Score
              </Text>
            </VStack>
          </CircularProgressLabel>
        </CircularProgress>
        <Badge
          colorScheme={getScoreColorScheme(displayReport.atsScore)}
          px={3}
          py={1}
          borderRadius="full"
        >
          {getScoreLabel(displayReport.atsScore)}
        </Badge>
      </VStack>
    );
  };

  const renderSummary = () => {
    if (!hasReport && !isStreaming) return null;

    return (
      <Box>
        <SectionHeader icon={<Icon type="Summary" size={16} />} title="Summary" />
        {hasSummary && displayReport?.overallSummary ? (
          <Box
            p={4}
            borderRadius="md"
            backgroundColor="whiteAlpha.50"
            border="1px solid"
            borderColor="whiteAlpha.100"
          >
            <Text fontSize="small" lineHeight="1.6">
              {displayReport.overallSummary}
            </Text>
          </Box>
        ) : (
          <SectionSkeleton />
        )}
      </Box>
    );
  };

  const renderATSTips = () => {
    if (!hasReport && !isStreaming) return null;

    const score = displayReport?.atsScore ?? 0;
    const tips = hasAtsScore && score > 0 ? getATSTips(score) : [];

    return (
      <Box>
        <SectionHeader icon={<Icon type="CheckMark" size={16} />} title="ATS Compatibility Tips" />
        {hasAtsScore && tips.length > 0 ? (
          <VStack spacing={2} alignItems="stretch">
            {tips.map((tip, i) => (
              <HStack
                key={i}
                p={3}
                borderRadius="md"
                backgroundColor="whiteAlpha.50"
                border="1px solid"
                borderColor="whiteAlpha.100"
                alignItems="flex-start"
                spacing={3}
              >
                <Box
                  minWidth="20px"
                  height="20px"
                  borderRadius="full"
                  backgroundColor={i < 3 ? 'purple.500' : 'whiteAlpha.200'}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  mt="1px"
                >
                  <Box
                    style={{
                      fontSize: '10px',
                      fontWeight: 700,
                      color: i < 3 ? 'white' : 'var(--chakra-colors-whiteAlpha-600)',
                    }}
                  >
                    {i + 1}
                  </Box>
                </Box>
                <Text fontSize="small" opacity={i < 3 ? 'primary' : 'secondary'} lineHeight="1.5">
                  {tip}
                </Text>
              </HStack>
            ))}
          </VStack>
        ) : (
          <VStack spacing={2} alignItems="stretch">
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                height="48px"
                borderRadius="md"
                startColor="whiteAlpha.100"
                endColor="whiteAlpha.200"
              />
            ))}
          </VStack>
        )}
      </Box>
    );
  };

  const renderSectionBreakdown = () => {
    if (!hasReport && !isStreaming) return null;

    return (
      <Box>
        <SectionHeader icon={<Icon type="Technologies" size={16} />} title="Section Analysis" />
        {hasBreakdown && displayReport && displayReport.sectionBreakdown.length > 0 ? (
          <Accordion allowMultiple defaultIndex={[0]}>
            {displayReport.sectionBreakdown.map((section, i) => (
              <AccordionItem key={i} borderColor="whiteAlpha.100">
                <AccordionButton py={3}>
                  <HStack flex={1} spacing={2}>
                    <Text fontSize="small" fontWeight="medium">
                      {section.sectionTitle}
                    </Text>
                    <Badge colorScheme={getRatingColor(section.rating)} size="sm">
                      {section.rating}
                    </Badge>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <Text fontSize="small" opacity="secondary" lineHeight="1.6">
                    {section.feedback}
                  </Text>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <VStack spacing={1} alignItems="stretch">
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                height="44px"
                borderRadius="md"
                startColor="whiteAlpha.100"
                endColor="whiteAlpha.200"
              />
            ))}
          </VStack>
        )}
      </Box>
    );
  };

  const renderMissingKeywords = () => {
    if (!hasReport && !isStreaming) return null;

    return (
      <Box>
        <SectionHeader icon={<Icon type="Warning" size={16} />} title="Missing Keywords" />
        {hasKeywords && displayReport && displayReport.missingKeywords.length > 0 ? (
          <Wrap spacing={2}>
            {displayReport.missingKeywords.map((keyword) => (
              <WrapItem key={keyword}>
                <Badge
                  variant="outline"
                  colorScheme="orange"
                  px={2}
                  py={1}
                  borderRadius="full"
                  fontSize="xs"
                >
                  {keyword}
                </Badge>
              </WrapItem>
            ))}
          </Wrap>
        ) : (
          <HStack spacing={2}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton
                key={i}
                width={`${50 + i * 12}px`}
                height="28px"
                borderRadius="full"
                startColor="whiteAlpha.100"
                endColor="whiteAlpha.200"
              />
            ))}
          </HStack>
        )}
      </Box>
    );
  };

  const renderSuggestions = () => {
    if (!hasReport && !isStreaming) return null;

    return (
      <Box>
        <SectionHeader icon={<Icon type="Lightning" size={16} />} title="Improvement Suggestions" />
        {hasSuggestions && displayReport && displayReport.suggestions.length > 0 ? (
          <List spacing={3}>
            {displayReport.suggestions.map((suggestion, i) => (
              <ListItem key={i}>
                <HStack alignItems="flex-start" spacing={3}>
                  <Box
                    minWidth="24px"
                    height="24px"
                    borderRadius="full"
                    backgroundColor="purple.400"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Text fontSize="xMicro" fontWeight="bold" color="white">
                      {i + 1}
                    </Text>
                  </Box>
                  <Text fontSize="small" opacity="secondary" lineHeight="1.6">
                    {suggestion}
                  </Text>
                </HStack>
              </ListItem>
            ))}
          </List>
        ) : (
          <VStack spacing={3} alignItems="stretch">
            {[1, 2, 3].map((i) => (
              <HStack key={i} spacing={3}>
                <Skeleton
                  width="24px"
                  height="24px"
                  borderRadius="full"
                  startColor="whiteAlpha.100"
                  endColor="whiteAlpha.200"
                  flexShrink={0}
                />
                <SkeletonText
                  noOfLines={2}
                  spacing="2"
                  flex={1}
                  startColor="whiteAlpha.100"
                  endColor="whiteAlpha.200"
                />
              </HStack>
            ))}
          </VStack>
        )}
      </Box>
    );
  };

  const renderLastGenerated = () => {
    if (!displayReport || isStreaming) return null;

    return (
      <Box pt={4} borderTop="1px solid" borderColor="whiteAlpha.100">
        <Text fontSize="micro" opacity="tertiary">
          Last generated:{' '}
          {new Date(displayReport.generatedAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </Text>
      </Box>
    );
  };

  const renderStreamingContent = () => (
    <VStack spacing={6} alignItems="stretch">
      {renderATSScore()}
      {renderATSTips()}
      {renderSummary()}
      {renderSectionBreakdown()}
      {renderMissingKeywords()}
      {renderSuggestions()}
      {renderLastGenerated()}
    </VStack>
  );

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent backgroundColor="gray.800" borderLeftColor="whiteAlpha.200">
        <DrawerHeader borderBottom="1px solid" borderColor="whiteAlpha.100" pb={3} pr={4}>
          {/* Title + Regenerate + Close */}
          <HStack justifyContent="space-between" alignItems="center">
            <HStack spacing={3} alignItems="center">
              <Text fontSize="large" fontWeight="semibold">
                AI CV Review
              </Text>
              {hasReport && !isStreaming && (
                <ProtectedElement
                  scheme="gradient"
                  size="sm"
                  onClick={handleGenerateReview}
                  isLoading={isStreaming}
                  requiredTokens={1}
                >
                  Regenerate
                </ProtectedElement>
              )}
            </HStack>
            <Box
              as="button"
              onClick={onClose}
              display="flex"
              alignItems="center"
              justifyContent="center"
              p={1}
              borderRadius="md"
              _hover={{ backgroundColor: 'whiteAlpha.100' }}
              transition="background-color 0.15s"
            >
              <Icon type="Close" size={16} />
            </Box>
          </HStack>
          {/* Streaming progress bar — hide once done event received */}
          {isStreaming && !isDone && (
            <Box mt={3}>
              <Progress
                value={progressPercent}
                size="xs"
                colorScheme="purple"
                backgroundColor="whiteAlpha.100"
                borderRadius="full"
                transition="all 0.5s ease"
              />
              {reviewStage && (
                <Text fontSize="xMicro" opacity="tertiary" mt={1}>
                  {STAGE_LABELS[reviewStage] || 'Processing...'}
                </Text>
              )}
            </Box>
          )}
        </DrawerHeader>
        <DrawerBody py={6}>
          {hasReport || isStreaming ? renderStreamingContent() : renderEmptyState()}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default AIReportPanel;
